import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Popover,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { DeleteOutline, PersonOutline, PersonOffOutlined } from "@mui/icons-material";
import { ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { EmailRecipient, MAX_RECIPIENTS } from "./types";
import { isRecipientInvalid, isValidEmail, newRecipientKey } from "./template";
import { EmailContact, fetchEmailContacts } from "./api";

interface RecipientsInputProps {
  value: EmailRecipient[];
  onChange: (recipients: EmailRecipient[]) => void;
}

const SEPARATORS = /[\s,;]+/;
const MAX_SUGGESTIONS = 6;

const normalize = (s: string) =>
  s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();

export default function RecipientsInput({ value, onChange }: RecipientsInputProps) {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [input, setInput] = useState("");
  const [focused, setFocused] = useState(false);
  const [editing, setEditing] = useState<{ key: string; anchor: HTMLElement } | null>(null);
  const full = value.length >= MAX_RECIPIENTS;
  const editingRecipient = value.find((r) => r.key === editing?.key);

  const [contacts, setContacts] = useState<EmailContact[]>([]);
  const [highlight, setHighlight] = useState(0);
  const [navigated, setNavigated] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchEmailContacts().then((data) => alive && setContacts(data));
    return () => {
      alive = false;
    };
  }, []);

  const query = normalize(input.trim());
  const added = new Set(value.map((r) => r.email.toLowerCase()));
  const suggestions = query
    ? contacts
        .filter(
          (c) =>
            !added.has(c.email.toLowerCase()) &&
            (normalize(c.email).includes(query) || normalize(c.nome ?? "").includes(query)),
        )
        .slice(0, MAX_SUGGESTIONS)
    : [];
  const showSuggestions = focused && !full && suggestions.length > 0;

  const pick = (c: EmailContact) => {
    onChange([...value, { key: newRecipientKey(), email: c.email, nome: c.nome ?? "", ativo: true }]);
    setInput("");
  };

  const changeInput = (text: string) => {
    setInput(text);
    setHighlight(0);
    setNavigated(false);
  };

  const addEmails = (raw: string) => {
    const known = new Set(value.map((r) => r.email.toLowerCase()));
    const next = [...value];
    raw
      .split(SEPARATORS)
      .map((e) => e.trim().replace(/^<|>$/g, ""))
      .filter(Boolean)
      .forEach((email) => {
        if (next.length >= MAX_RECIPIENTS || known.has(email.toLowerCase())) return;
        known.add(email.toLowerCase());
        next.push({ key: newRecipientKey(), email, nome: "", ativo: true });
      });
    if (next.length !== value.length) onChange(next);
  };

  const commit = () => {
    if (!input.trim()) return;
    addEmails(input);
    setInput("");
  };

  const update = (key: string, patch: Partial<EmailRecipient>) =>
    onChange(value.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const remove = (key: string) => onChange(value.filter((r) => r.key !== key));

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (showSuggestions) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const step = e.key === "ArrowDown" ? 1 : -1;
        setHighlight((h) => (h + step + suggestions.length) % suggestions.length);
        setNavigated(true);
        return;
      }
      // A typed complete e-mail wins over a suggestion unless the user arrowed to one.
      const chosen = suggestions[Math.min(highlight, suggestions.length - 1)];
      if ((e.key === "Enter" || e.key === "Tab") && (navigated || !isValidEmail(input.trim()))) {
        e.preventDefault();
        pick(chosen);
        return;
      }
      // Let names with spaces be typed while there are matches.
      if (e.key === " ") return;
    }
    if (["Enter", "Tab", ",", ";", " "].includes(e.key)) {
      if (input.trim()) {
        e.preventDefault();
        commit();
      }
    } else if (e.key === "Backspace" && !input && value.length) {
      remove(value[value.length - 1].key);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text");
    if (!SEPARATORS.test(text.trim())) return;
    e.preventDefault();
    addEmails(`${input} ${text}`);
    setInput("");
  };

  const chipTooltip = (r: EmailRecipient) => {
    if (r.rejeitado) return "E-mail recusado pelo servidor";
    if (!isValidEmail(r.email)) return "E-mail inválido";
    if (!r.ativo) return "Pausado — não receberá os próximos envios";
    return r.nome ? `${r.nome} · clique para editar` : "Clique para adicionar nome ou pausar";
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, position: "relative" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "1rem", fontWeight: 500 }}>Para quem enviar?</Typography>
        <Typography fontSize={12} color={full ? "warning.dark" : "text.secondary"}>
          {value.length}/{MAX_RECIPIENTS}
        </Typography>
      </Box>
      <Box
        onClick={() => inputRef.current?.focus()}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 0.75,
          minHeight: 44,
          px: 1,
          py: 0.75,
          borderRadius: "8px",
          cursor: "text",
          backgroundColor: theme.palette.background.textField,
          border: `1px solid ${focused ? theme.palette.primary.main : "transparent"}`,
          transition: "border-color .15s",
        }}
      >
        {value.map((r) => {
          const invalid = isRecipientInvalid(r);
          return (
            <Tooltip key={r.key} title={chipTooltip(r)} arrow>
              <Chip
                size="small"
                icon={
                  r.ativo ? (
                    <PersonOutline fontSize="small" />
                  ) : (
                    <PersonOffOutlined fontSize="small" />
                  )
                }
                label={r.nome ? `${r.nome} <${r.email}>` : r.email}
                variant={r.ativo ? "filled" : "outlined"}
                color={invalid ? "error" : "default"}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing({ key: r.key, anchor: e.currentTarget });
                }}
                onDelete={() => remove(r.key)}
                sx={{
                  maxWidth: "100%",
                  borderRadius: "6px",
                  ...(invalid
                    ? {}
                    : {
                        backgroundColor: r.ativo
                          ? theme.palette.background.paper
                          : "transparent",
                        color: r.ativo ? "text.primary" : "text.disabled",
                        textDecoration: r.ativo ? "none" : "line-through",
                      }),
                }}
              />
            </Tooltip>
          );
        })}
        <Box
          component="input"
          ref={inputRef}
          value={input}
          disabled={full}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => changeInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            commit();
          }}
          placeholder={
            full
              ? "Limite de destinatários atingido"
              : value.length
                ? "Adicionar outro e-mail"
                : "Digite ou cole e-mails"
          }
          type="text"
          inputMode="email"
          role="combobox"
          aria-label="Destinatários"
          aria-autocomplete="list"
          aria-expanded={showSuggestions}
          aria-controls={showSuggestions ? "recipients-suggestions" : undefined}
          aria-activedescendant={
            showSuggestions
              ? `recipients-option-${Math.min(highlight, suggestions.length - 1)}`
              : undefined
          }
          autoComplete="off"
          sx={{
            flex: 1,
            minWidth: 160,
            border: "none",
            outline: "none",
            background: "transparent",
            font: "inherit",
            fontSize: 14,
            color: theme.palette.text.primary,
            py: 0.5,
            px: 0.5,
            "&::placeholder": { color: theme.palette.text.disabled, opacity: 1 },
          }}
        />
      </Box>

      {showSuggestions && (
        <Paper
          role="listbox"
          id="recipients-suggestions"
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            mt: 0.5,
            py: 0.5,
            zIndex: theme.zIndex.modal + 1,
            border: `1px solid ${theme.palette.stroke}`,
          }}
        >
          {suggestions.map((c, i) => (
            <MenuItem
              key={c.email}
              id={`recipients-option-${i}`}
              role="option"
              aria-selected={i === Math.min(highlight, suggestions.length - 1)}
              selected={i === Math.min(highlight, suggestions.length - 1)}
              // mousedown keeps focus so the input's onBlur doesn't commit the half-typed text
              onMouseDown={(e) => {
                e.preventDefault();
                pick(c);
              }}
              onMouseEnter={() => {
                setHighlight(i);
                setNavigated(true);
              }}
              sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", py: 0.5 }}
            >
              <Typography fontSize={14}>{c.nome || c.email}</Typography>
              {c.nome && (
                <Typography fontSize={12} color="text.secondary">
                  {c.email}
                </Typography>
              )}
            </MenuItem>
          ))}
        </Paper>
      )}

      <Popover
        open={!!editingRecipient}
        anchorEl={editing?.anchor}
        onClose={() => setEditing(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              width: 300,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              border: `1px solid ${theme.palette.stroke}`,
            },
          },
        }}
      >
        {editingRecipient && (
          <>
            <TextField
              size="small"
              label="E-mail"
              value={editingRecipient.email}
              error={isRecipientInvalid(editingRecipient)}
              onChange={(e) =>
                update(editingRecipient.key, { email: e.target.value.trim(), rejeitado: false })
              }
            />
            <TextField
              size="small"
              label="Nome (opcional)"
              placeholder="Ex.: João"
              autoFocus
              value={editingRecipient.nome}
              onChange={(e) => update(editingRecipient.key, { nome: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && setEditing(null)}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderRadius: 1,
                px: 1,
                backgroundColor: alpha(theme.palette.text.primary, 0.04),
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={editingRecipient.ativo}
                    onChange={(e) => update(editingRecipient.key, { ativo: e.target.checked })}
                  />
                }
                label={<Typography fontSize={14}>Receber envios</Typography>}
              />
              <Button
                size="small"
                color="error"
                startIcon={<DeleteOutline fontSize="small" />}
                onClick={() => {
                  remove(editingRecipient.key);
                  setEditing(null);
                }}
              >
                Remover
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </Box>
  );
}
